import React, { useEffect, useState } from 'react';
import { useUser } from '../UserContext';
import {
  AI_PIPELINE_BLURBS,
  AI_PIPELINE_LABELS,
  AI_PIPELINE_LEGACY,
  AI_PIPELINE_MODES,
  AI_PIPELINE_SCHEMA_V1,
  AI_PIPELINE_SCHEMA_V2,
} from '../constants/aiPipelineModes';
import { getAiPipelineSettings, setAiPipelineMode } from '../services/aiPipelineSettings';
import {
  backfillSchoolSchemasFromRegistration,
  harvestSchoolSchemas,
  buildSoftEvidenceAndClaimsMap,
} from '../services/aiPipelines/schemaV2';

/**
 * Staff switch between legacy, schema_v1, and schema_v2 AI pipelines.
 * Default remains legacy; modes are additive ringfences (do not remove each other).
 */
const AdminAiPipeline = () => {
  const { user } = useUser();
  const [mode, setMode] = useState(AI_PIPELINE_LEGACY);
  const [meta, setMeta] = useState({ updatedAt: null, updatedBy: null, note: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [toolBusy, setToolBusy] = useState(null);
  const [claimsSchoolId, setClaimsSchoolId] = useState('');

  const load = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const s = await getAiPipelineSettings();
      setMode(s.mode);
      setMeta({ updatedAt: s.updatedAt, updatedBy: s.updatedBy, note: s.note });
    } catch (err) {
      setStatus({ type: 'error', msg: err?.message || 'Failed to load pipeline setting.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (nextMode) => {
    setSaving(true);
    setStatus(null);
    try {
      const saved = await setAiPipelineMode(nextMode, {
        uid: user?.uid,
        email: user?.email,
      });
      setMode(saved);
      const msg =
        saved === AI_PIPELINE_SCHEMA_V2
          ? 'Schema v2 active. Legacy and v1 remain available — switch anytime.'
          : saved === AI_PIPELINE_SCHEMA_V1
            ? 'Schema v1 pipeline active. Legacy code is unchanged — switch back anytime.'
            : 'Legacy pipeline restored. Schema v1/v2 modules remain available.';
      setStatus({ type: 'success', msg });
      await load();
    } catch (err) {
      setStatus({ type: 'error', msg: err?.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const runTool = async (key, fn) => {
    setToolBusy(key);
    setStatus(null);
    try {
      const result = await fn();
      setStatus({
        type: 'success',
        msg: typeof result === 'string' ? result : JSON.stringify(result),
      });
    } catch (err) {
      setStatus({ type: 'error', msg: err?.message || 'Tool failed.' });
    } finally {
      setToolBusy(null);
    }
  };

  return (
    <div style={{ display: 'grid', gap: '18px', maxWidth: 720 }}>
      <div
        style={{
          border: '1px solid rgba(19, 138, 138, 0.35)',
          borderRadius: '10px',
          background: 'rgba(2, 6, 23, 0.45)',
          padding: '18px',
        }}
      >
        <h3 style={{ margin: '0 0 10px 0', color: '#dbeafe' }}>AI Pipeline</h3>
        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.86rem', lineHeight: 1.5 }}>
          Ringfenced switch for SEF pre-submit diagnostic, evidence distill/refinery, and report draft
          generation. Legacy and Schema v1 stay intact; Schema v2 is additive. See{' '}
          <code>docs/ai-pipeline-ringfence.md</code> and <code>docs/ai-pipeline-v2-plan.md</code>.
        </p>
      </div>

      <div
        style={{
          border: '1px solid rgba(148, 163, 184, 0.25)',
          borderRadius: '10px',
          background: 'rgba(15, 23, 42, 0.65)',
          padding: '18px',
        }}
      >
        <h4 style={{ margin: '0 0 12px 0', color: '#e2e8f0' }}>Active mode</h4>
        {loading ? (
          <p style={{ color: '#94a3b8', margin: 0 }}>Loading…</p>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {AI_PIPELINE_MODES.map((id) => (
              <label
                key={id}
                style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  padding: '12px',
                  borderRadius: '8px',
                  border:
                    mode === id
                      ? '1px solid rgba(45, 212, 191, 0.55)'
                      : '1px solid rgba(51, 65, 85, 0.8)',
                  background: mode === id ? 'rgba(13, 148, 136, 0.15)' : 'transparent',
                  cursor: saving ? 'wait' : 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="ai-pipeline-mode"
                  value={id}
                  checked={mode === id}
                  disabled={saving}
                  onChange={() => handleSave(id)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong style={{ color: '#e2e8f0' }}>{AI_PIPELINE_LABELS[id]}</strong>
                  <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: 4 }}>
                    {AI_PIPELINE_BLURBS[id]}
                  </div>
                </span>
              </label>
            ))}
          </div>
        )}

        {meta.note ? (
          <p style={{ margin: '14px 0 0', color: '#64748b', fontSize: '0.8rem' }}>
            {meta.note}
            {meta.updatedBy ? ` · last set by ${meta.updatedBy}` : ''}
          </p>
        ) : null}

        {status ? (
          <p
            style={{
              margin: '14px 0 0',
              color: status.type === 'error' ? '#fca5a5' : '#6ee7b7',
              fontSize: '0.86rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {status.msg}
          </p>
        ) : null}
      </div>

      <div
        style={{
          border: '1px solid rgba(148, 163, 184, 0.25)',
          borderRadius: '10px',
          background: 'rgba(15, 23, 42, 0.65)',
          padding: '18px',
        }}
      >
        <h4 style={{ margin: '0 0 8px 0', color: '#e2e8f0' }}>Schema v2 tools</h4>
        <p style={{ margin: '0 0 14px', color: '#94a3b8', fontSize: '0.82rem', lineHeight: 1.45 }}>
          Backfill registration seeds, rebuild SEF claims/soft evidence for one school, or harvest
          anonymised field-fill stats across schools.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          <button
            type="button"
            className="btn-secondary"
            disabled={!!toolBusy}
            onClick={() =>
              runTool('backfill', async () => {
                const r = await backfillSchoolSchemasFromRegistration({ max: 300 });
                return `Backfill done: seeded ${r.seeded}/${r.attempted} (skipped ${r.skipped}, errors ${r.errors.length}).`;
              })
            }
          >
            {toolBusy === 'backfill' ? 'Seeding…' : 'Backfill registration → schemas'}
          </button>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={claimsSchoolId}
              onChange={(e) => setClaimsSchoolId(e.target.value)}
              placeholder="School id for claims rebuild"
              style={{
                flex: 1,
                minWidth: 180,
                background: '#0f172a',
                border: '1px solid #334155',
                color: '#e2e8f0',
                padding: '8px 10px',
                borderRadius: 6,
              }}
            />
            <button
              type="button"
              className="btn-secondary"
              disabled={!!toolBusy || !claimsSchoolId.trim()}
              onClick={() =>
                runTool('claims', async () => {
                  const r = await buildSoftEvidenceAndClaimsMap(claimsSchoolId.trim());
                  return `Claims map rebuilt: ${r.claimsMap?.length || 0} claims, support ${r.claimMetrics?.supportRatePct ?? '?'}%.`;
                })
              }
            >
              {toolBusy === 'claims' ? 'Building…' : 'Rebuild claims / soft evidence'}
            </button>
          </div>

          <button
            type="button"
            className="btn-secondary"
            disabled={!!toolBusy}
            onClick={() =>
              runTool('harvest', async () => {
                const r = await harvestSchoolSchemas({
                  max: 300,
                  actorUid: user?.uid,
                  actorEmail: user?.email,
                });
                return `Harvest wrote schemaHarvest/latest (${r.schoolCount} schools, ${r.fieldCount} field ids).`;
              })
            }
          >
            {toolBusy === 'harvest' ? 'Harvesting…' : 'Harvest anonymised schema fill'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAiPipeline;